import clsx from "clsx";
import { FC, ReactNode } from "react";

import type { CheckpointEvent } from "@tsmono/inspect-common/types";
import { MetaDataGrid } from "@tsmono/inspect-components/content";
import { formatBytes, formatMs, formatNumber, formatTime } from "@tsmono/util";

import styles from "./CheckpointEventView.module.css";
import { EventPanel } from "./event/EventPanel";
import { EventNode, EventPanelCallbacks } from "./types";

type SnapshotDetails = CheckpointEvent["host"];

interface CheckpointEventViewProps {
  eventNode: EventNode<CheckpointEvent>;
  className?: string;
  eventCallbacks?: EventPanelCallbacks;
}

const asNumber = (v: unknown): number | null =>
  typeof v === "number" ? v : null;

// Human-readable sentence for the known trigger kinds and their
// metadata shapes; null falls back to the raw kind + metadata grid so
// new trigger kinds/keys degrade gracefully rather than render wrong.
const triggerSummary = (event: CheckpointEvent): string | null => {
  const md: Record<string, unknown> = event.trigger_metadata ?? {};
  switch (event.trigger) {
    case "token": {
      const every = asNumber(md["every"]);
      const since = asNumber(md["tokens_since_last_fire"]);
      return every !== null && since !== null
        ? `token — ${formatNumber(every)} (${formatNumber(since)})`
        : null;
    }
    case "time": {
      const every = asNumber(md["every_seconds"]);
      const elapsed = asNumber(md["elapsed_seconds"]);
      return every !== null && elapsed !== null
        ? `time — ${formatTime(every)} (${formatTime(elapsed)})`
        : null;
    }
    case "turn": {
      const every = asNumber(md["every"]);
      return every !== null ? `turn — ${formatNumber(every)}` : null;
    }
    case "manual":
      return "manual";
    case "agent_complete":
      return "agent complete";
    default:
      return null;
  }
};

const Row: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <>
    <div className={clsx("text-style-label", "text-style-secondary")}>
      {label}
    </div>
    <div className={styles.rowValue}>{children}</div>
  </>
);

const Snapshot: FC<{ details: SnapshotDetails }> = ({ details }) => (
  <>
    <div>
      {formatBytes(details.size_bytes)} · {formatMs(details.duration_ms)}
    </div>
    {details.files && details.files.length > 0 ? (
      <div className={styles.files}>
        {details.files.map((file) => (
          <div key={file} className={styles.file}>
            {file}
          </div>
        ))}
        {details.additional_files ? (
          <div className={styles.fileOverflow}>
            +{details.additional_files} more
          </div>
        ) : null}
      </div>
    ) : null}
  </>
);

export const CheckpointEventView: FC<CheckpointEventViewProps> = ({
  eventNode,
  className,
  eventCallbacks,
}) => {
  const event = eventNode.event;
  const summary = triggerSummary(event);
  const metadata: Record<string, unknown> = event.trigger_metadata ?? {};
  const sandboxEntries = Object.entries(event.sandboxes);

  return (
    <EventPanel
      eventNodeId={eventNode.id}
      className={className}
      title={`Checkpoint ${event.checkpoint_id} — turn ${event.turn} (${formatBytes(
        event.size_bytes
      )}, ${formatTime(event.duration_ms / 1000)})`}
      icon="checkpoint"
      eventCallbacks={eventCallbacks}
      collapsibleContent
    >
      <div className={clsx(styles.panel, "text-size-small")}>
        <Row label="Trigger">
          <div>{summary ?? event.trigger}</div>
          {summary === null && Object.keys(metadata).length > 0 ? (
            <MetaDataGrid
              entries={metadata}
              options={{ plain: true, size: "mini" }}
            />
          ) : null}
        </Row>
        <Row label="Host">
          <Snapshot details={event.host} />
        </Row>
        {sandboxEntries.map(([name, details]) => (
          <Row
            key={name}
            label={sandboxEntries.length === 1 ? "Sandbox" : name}
          >
            <Snapshot details={details} />
          </Row>
        ))}
      </div>
    </EventPanel>
  );
};
